import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpDown, Plus, Search, Unplug } from 'lucide-react';
import type { DocumentPriority } from '../lib/types';
import { Pill, priorityStyles, priorityRank } from './DocTypeBlock';

export interface DocTypeOption {
    id: string;
    name: string;
    description: string;
    section_id: string;
    section_name: string;
    priority: DocumentPriority;
    pending: boolean;
}

type CommonProps = {
    allTypes: DocTypeOption[];
};

type ComboboxProps = CommonProps & {
    mode: 'combobox';
    excludeDefinitions: Set<string>;
    onLink: (definitionId: string) => Promise<void>;
    // Admin-only; the caller gates it.
    canCreate?: boolean;
    // Fire `onLink` on click with no Link button, for adding a suggestion (the folder page).
    immediate?: boolean;
    placeholder?: string;
};

type LibraryProps = CommonProps & {
    mode: 'library';
};

type Props = ComboboxProps | LibraryProps;

function filterTypes(allTypes: DocTypeOption[], query: string, exclude?: Set<string>) {
    const q = query.toLowerCase().trim();
    return allTypes
        .filter(t => !exclude || !exclude.has(t.id))
        .filter(t => !q
            || t.name.toLowerCase().includes(q)
            || t.description.toLowerCase().includes(q)
            || t.section_name.toLowerCase().includes(q));
}

/**
 * Doc-type search + selector. Two modes:
 *
 * - `combobox`: floating dropdown that filters on focus, click to select, then
 *   the "Link" button calls `onLink`. Used on FileDetailPage to attach a file
 *   to a doc type.
 * - `library`: always-visible filterable list. Each row is a link to that
 *   type's detail page. Used on the System → Library tab.
 */
export default function DocTypeCombobox(props: Props) {
    if (props.mode === 'library') {
        return <LibraryView allTypes={props.allTypes} />;
    }
    return <ComboboxView {...props} />;
}

function ComboboxView({ allTypes, excludeDefinitions, onLink, canCreate, immediate, placeholder }: ComboboxProps) {
    const [query, setQuery] = useState('');
    const [selected, setSelected] = useState<DocTypeOption | null>(null);
    const [open, setOpen] = useState(false);
    const [linking, setLinking] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const filtered = useMemo(
        () => filterTypes(allTypes, query, excludeDefinitions).slice(0, 100),
        [allTypes, excludeDefinitions, query],
    );

    const handleLinkClick = async () => {
        if (!selected) return;
        setLinking(true);
        try {
            await onLink(selected.id);
            setSelected(null);
            setQuery('');
        } finally {
            setLinking(false);
        }
    };

    const displayText = selected ? selected.name : query;

    return (
        <div ref={wrapRef} className="relative">
            <div className="flex items-stretch gap-2">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-coolslate pointer-events-none" />
                    <input
                        type="text"
                        value={displayText}
                        onChange={e => {
                            setQuery(e.target.value);
                            setSelected(null);
                            setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        placeholder={placeholder ?? 'Search document types...'}
                        className="w-full pl-9 pr-3 py-2.5 text-sm border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand"
                    />
                </div>
                {!immediate && (
                    <button
                        onClick={handleLinkClick}
                        disabled={!selected || linking}
                        className="flex items-center gap-1.5 px-4 text-sm font-medium text-insight bg-insight/8 hover:bg-insight/15 border border-insight/20 rounded-md transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <Unplug className="w-3.5 h-3.5" />
                        {linking ? 'Linking...' : 'Link'}
                    </button>
                )}
            </div>
            {open && (
                <div className="absolute z-10 left-0 right-0 mt-1 max-h-80 overflow-y-auto bg-white border border-softgrey rounded-lg shadow-md">
                    {filtered.length > 0 ? (
                        filtered.map(t => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => {
                                    if (immediate) {
                                        onLink(t.id);
                                    } else {
                                        setSelected(t);
                                    }
                                    setQuery('');
                                    setOpen(false);
                                }}
                                className="w-full text-left px-3 py-2.5 border-b border-softgrey last:border-b-0 hover:bg-offwhite transition-colors"
                            >
                                <span className="text-sm font-medium text-graphite truncate block">{t.name}</span>
                                {t.description && (
                                    <p className="text-[12px] text-coolslate mt-0.5 truncate">{t.description}</p>
                                )}
                            </button>
                        ))
                    ) : (
                        <div className="px-3 py-3 text-sm text-coolslate">
                            No matching document types.
                        </div>
                    )}
                    {canCreate && (
                        <Link
                            to="/types/add"
                            className="flex items-center gap-2 px-3 py-2.5 border-t border-softgrey text-sm font-medium text-brand-dark hover:bg-offwhite transition-colors"
                        >
                            <Plus className="w-4 h-4 shrink-0" />
                            Add a new document type
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}

type SortBy = 'name' | 'section' | 'priority';

function LibraryView({ allTypes }: { allTypes: DocTypeOption[] }) {
    const [query, setQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortBy>('name');

    const filtered = useMemo(() => {
        const rows = filterTypes(allTypes, query);
        const sorted = [...rows];
        if (sortBy === 'name') {
            sorted.sort((a, b) => a.name.localeCompare(b.name));
        } else if (sortBy === 'section') {
            sorted.sort((a, b) =>
                a.section_name.localeCompare(b.section_name) || a.name.localeCompare(b.name));
        } else {
            sorted.sort((a, b) =>
                priorityRank[a.priority] - priorityRank[b.priority] || a.name.localeCompare(b.name));
        }
        return sorted;
    }, [allTypes, query, sortBy]);

    return (
        <div>
            <div className="flex items-stretch gap-2 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-coolslate pointer-events-none" />
                    <input
                        type="text"
                        value={query}
                        onChange={e => setQuery(e.target.value)}
                        placeholder="Filter document types..."
                        className="w-full pl-9 pr-3 py-2.5 text-sm border border-softgrey rounded-lg bg-white text-graphite focus:outline-none focus:border-brand"
                    />
                </div>
                <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-coolslate pointer-events-none" />
                    <select
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value as SortBy)}
                        className="appearance-none pl-9 pr-3 py-2.5 text-sm border border-softgrey rounded-lg bg-white text-graphite cursor-pointer hover:border-brand/40 focus:outline-none focus:border-brand"
                    >
                        <option value="name">Sort: Alphabetical</option>
                        <option value="section">Sort: Section</option>
                        <option value="priority">Sort: Priority</option>
                    </select>
                </div>
            </div>
            {filtered.length === 0 ? (
                <div className="py-12 text-center text-sm text-coolslate">
                    No matching document types.
                </div>
            ) : (
                <div className="space-y-1.5">
                    {filtered.map(t => (
                        <Link
                            key={t.id}
                            to={`/types/${t.id}`}
                            className="flex items-center gap-3 px-3.5 py-3 border border-sage/30 bg-sage/5 hover:bg-sage/10 rounded-lg transition-colors group"
                        >
                            <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-graphite group-hover:text-brand-dark transition-colors truncate block">
                                    {t.name}
                                </span>
                                {t.description && (
                                    <span className="text-[12px] text-coolslate truncate block mt-0.5">
                                        {t.description}
                                    </span>
                                )}
                            </div>
                            {t.section_name && (
                                <span className="text-[12px] text-coolslate shrink-0 truncate max-w-[180px]" title={t.section_name}>
                                    {t.section_name}
                                </span>
                            )}
                            <Pill style={priorityStyles[t.priority]} shrink />
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
